import type { AnalyzedDocument } from '../analyze';
import type { JsonNode } from '../json/tree';
import type { Token } from '../json/tokens';
import type { DiagnosticCode } from '../lint/codes';
import type { Diagnostic, FixConfidence, Params, Position, Span } from '../lint/diagnostic';
import type { LintReport } from '../lint/report';
import type { LineIndex } from '../text/lineIndex';
import type { TextEdit } from '../text/textEdit';

/**
 * The nineteen repair rules (eighteen in v1; ADR-0011 amendment 2026-10-01). Adding a rule means adding a member here, a file under
 * `rules/`, a line in `registry.ts` and a template in `messages/en.ts` — and nothing else
 * (ADR-0011).
 */
export type RepairRuleId =
  | 'strip-bom'
  | 'remove-comment'
  | 'remove-trailing-comma'
  | 'remove-stray-comma'
  | 'restore-missing-quote'
  | 'insert-missing-comma'
  | 'insert-missing-colon'
  | 'quote-unquoted-key'
  | 'requote-single-quoted'
  | 'normalize-smart-quotes'
  | 'close-unterminated-string'
  | 'close-open-containers'
  | 'drop-extra-closers'
  | 'escape-control-character'
  | 'normalize-js-literal'
  | 'normalize-python-literal'
  | 'normalize-non-finite'
  | 'fix-number-literal'
  | 'drop-trailing-content';

/**
 * Declared in `lint/diagnostic.ts` because `Fix` needs it there; re-exported here because
 * this is where a rule author looks for it (docs/ARCHITECTURE.md §4.4).
 */
export type { FixConfidence };

/**
 * Everything a rule is allowed to read. Frozen and identical for every rule of one pass.
 *
 * Note `tokens`, not "a string you can run a regex over". A rule physically cannot express
 * "every comma before a `}`" — only "a `Comma` token whose next significant token is
 * `CloseBrace`". That is the whole point: the legacy fixer used regexes and silently
 * corrupted `{"a":"x, }"}` and `{"u":"https://x.com/a"}` (ADR-0011).
 */
export interface RepairContext {
  readonly source: string;
  readonly tokens: readonly Token[];
  readonly tree: JsonNode | undefined;
  readonly diagnostics: readonly Diagnostic[];
  readonly lines: LineIndex;
}

export interface RepairProposal {
  readonly ruleId: RepairRuleId;
  /** What to highlight as "this is what changes". */
  readonly span: Span;
  readonly edits: readonly TextEdit[];
  readonly labelCode: `fix.${RepairRuleId}`;
  readonly labelParams?: Params;
  readonly resolves: DiagnosticCode;
}

export interface RepairRule {
  readonly id: RepairRuleId;
  readonly confidence: FixConfidence;
  /** Lower wins an edit conflict. Conflicts are never resolved by guesswork (ADR-0012). */
  readonly priority: number;
  readonly resolves: readonly DiagnosticCode[];
  /** Pure. Does not throw. Returns `[]` when it has nothing to say. */
  detect(ctx: RepairContext): readonly RepairProposal[];
}

export interface RepairOptions {
  /** Default `false`: "Fix all" applies only `safe` rules (ADR-0012). */
  readonly allowHeuristic: boolean;
  readonly maxPasses: number;
  readonly disabled: ReadonlySet<RepairRuleId>;
}

export const DEFAULT_REPAIR_OPTIONS: RepairOptions = {
  allowHeuristic: false,
  maxPasses: 4,
  disabled: new Set<RepairRuleId>(),
};

export interface AppliedRepair {
  readonly ruleId: RepairRuleId;
  readonly pass: number;
  /** In the text as it was BEFORE this pass. */
  readonly at: Position;
  readonly labelCode: `fix.${RepairRuleId}`;
  readonly labelParams?: Params;
  readonly before: string;
  readonly after: string;
}

export interface SkippedRepair {
  readonly ruleId: RepairRuleId;
  readonly reason: 'heuristic-blocked' | 'edit-conflict' | 'disabled';
}

export interface RepairResult {
  readonly text: string;
  readonly changed: boolean;
  readonly applied: readonly AppliedRepair[];
  /** So the UI can say WHY a problem is still on screen. */
  readonly skipped: readonly SkippedRepair[];
  readonly before: LintReport;
  readonly after: LintReport;
  readonly passes: number;
  readonly stoppedBecause:
    'already-clean' | 'fixed-point' | 'no-progress' | 'max-passes' | 'reverted-unsafe';
}

export interface RepairEngine {
  /** Full run to a fixed point. */
  repair(source: string, options?: Partial<RepairOptions>): RepairResult;
  /** Proposals for an analysis that already exists — this is what the quick-fix menu uses. */
  proposalsFor(doc: AnalyzedDocument, options?: Partial<RepairOptions>): readonly RepairProposal[];
}

/**
 * `analyze` and `rules` are injected so that the DRIVER (conflict resolution, fixed point,
 * oscillation guard, the `reverted-unsafe` safety net) is tested against a FAKE rule set,
 * without a single real rule (ADR-0012).
 *
 * TODO(stage-core): implementation in `core/repair/engine.ts`, together with the plain
 * `repair(source, options?)` wrapper over `analyzeDocument` + `ALL_REPAIR_RULES`.
 */
export type CreateRepairEngine = (deps: {
  readonly analyze: (source: string) => AnalyzedDocument;
  readonly rules?: readonly RepairRule[];
}) => RepairEngine;
